import { Upload } from 'lucide-react';
import { lazy, Suspense, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '~/components/ui/button';
import { Card, CardContent } from '~/components/ui/card';
import { Skeleton } from '~/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger } from '~/components/ui/tabs';
import { cn } from '~/utils/cn';
import { downloadCsv } from '~/utils/csv';

import { useArmLabels } from '../../hooks/use-arm-labels';
import { useExperimentDailyLift } from '../../hooks/use-experiments';
import type { ArmSlot, DailyLiftRow } from '../../types/experiment.types';
import { formatPercentFromDecimal } from '../../utils/format-percent';

import { ExperimentDataUnavailable } from './experiment-data-unavailable';
import { ExperimentSeriesToggle } from './experiment-series-toggle';
import type { TrendSeries } from './experiment-trend-chart';
import { ExperimentTrendEmptyState } from './experiment-trend-empty-state';
import { RevenueSourceTag } from './revenue-source-tag';

// Lazy-loaded — echarts is heavy and should not download until the trend
// view is actually rendered (the table view is the default for some users).
const ExperimentTrendChart = lazy(() =>
  import('./experiment-trend-chart').then((m) => ({ default: m.ExperimentTrendChart })),
);

type ViewMode = 'trend' | 'table';

interface Props {
  experimentId: string;
  /** Encoded `?segment=` product filter (see `encodeSegmentFilter`); `undefined` = unscoped. */
  segment?: string | undefined;
}

const COLUMNS: { key: keyof Omit<DailyLiftRow, 'day'>; labelKey: string }[] = [
  { key: 'gmcImpr', labelKey: 'detail.dailyLift.columns.gmcImpr' },
  { key: 'adsImpr', labelKey: 'detail.dailyLift.columns.adsImpr' },
  { key: 'gmcClicks', labelKey: 'detail.dailyLift.columns.gmcClicks' },
  { key: 'adsClicks', labelKey: 'detail.dailyLift.columns.adsClicks' },
  { key: 'convValue', labelKey: 'detail.dailyLift.columns.convValue' },
  { key: 'conversions', labelKey: 'detail.dailyLift.columns.conversions' },
  { key: 'sessions', labelKey: 'detail.dailyLift.columns.sessions' },
  { key: 'revenue', labelKey: 'detail.dailyLift.columns.revenue' },
];

interface TrendSeriesMeta {
  id: ArmSlot;
  color: 'blue' | 'green' | 'orange';
  lineStyle: 'solid' | 'dashed';
}

// Slot ids only — each slot's display label (its domain arm letter) is
// resolved at render time via `useArmLabels`.
const TREND_SERIES_META: readonly TrendSeriesMeta[] = [
  { id: 'testA', color: 'blue', lineStyle: 'solid' },
  { id: 'testB', color: 'green', lineStyle: 'solid' },
  { id: 'control', color: 'orange', lineStyle: 'dashed' },
];

// Table cells render at 2-dp precision via the shared util. Values inside
// `[-0.0005, 0.0005]` (decimal) collapse to "0.00%" — these are below
// statistical significance for daily-lift DID metrics and rendering them
// as "+0.03%" would imply more signal than exists.
const formatPct = (value: number) => formatPercentFromDecimal(value, { precision: 'table-2dp' });

// Chart axis + tooltip — always-1-dp to keep neighbouring ticks visually
// consistent (the daily-lift trend spans small magnitudes where varying
// width is jarring).
const formatChartPct = (value: number) =>
  formatPercentFromDecimal(value, { precision: 'chart-1dp' });

// `toneFor` collapses to neutral inside the same near-zero threshold the
// table formatter does — otherwise a row that displays as "0.00%" gets a
// green or red colour, creating a tone-vs-text contradiction. Threshold
// is 0.0005 decimal (matches the formatter's 0.05% in percent space).
const TONE_NEUTRAL_THRESHOLD_DECIMAL = 0.0005;

function toneFor(value: number): string {
  if (!Number.isFinite(value) || Math.abs(value) <= TONE_NEUTRAL_THRESHOLD_DECIMAL) {
    return 'text-muted-foreground';
  }
  return value > 0 ? 'text-success font-medium' : 'text-error font-medium';
}

export function ExperimentDailyLiftTable({ experimentId, segment }: Props) {
  const { t } = useTranslation('experiments');
  const { data, isLoading, isError } = useExperimentDailyLift(experimentId, segment);
  // Wire test slots map onto domain arm letters: testA → Arm B, testB → Arm C.
  const { label: armLabel } = useArmLabels(experimentId);
  const [view, setView] = useState<ViewMode>('table');
  const [visibleSeriesIds, setVisibleSeriesIds] = useState(['testA', 'testB', 'control']);

  const trendXAxis = useMemo(
    () => (data ? data.trend.map((p) => `Day ${String(p.day)}`) : []),
    [data],
  );

  // Filter to arms the analytics layer marked present so we don't draw a
  // misleading 0% line for an arm that never had impressions — same rule
  // the cumulative-trend panel uses.
  const presentMeta = useMemo<readonly TrendSeriesMeta[]>(() => {
    if (!data) return TREND_SERIES_META;
    const present = new Set<ArmSlot>(data.armsPresent);
    return TREND_SERIES_META.filter((meta) => present.has(meta.id));
  }, [data]);

  const trendSeries = useMemo<TrendSeries[]>(() => {
    if (!data) return [];
    return presentMeta.map((meta) => ({
      id: meta.id,
      name: armLabel(meta.id),
      color: meta.color,
      lineStyle: meta.lineStyle,
      points: data.trend.map((p) => ({
        x: `Day ${String(p.day)}`,
        y: p[meta.id],
      })),
    }));
  }, [data, presentMeta, armLabel]);

  const toggleSeries = (id: string, checked: boolean) => {
    setVisibleSeriesIds((prev) =>
      checked
        ? [...prev, id].filter((v, i, arr) => arr.indexOf(v) === i)
        : prev.filter((v) => v !== id),
    );
  };

  const isExportDisabled =
    isLoading ||
    isError ||
    !data ||
    (view === 'table' ? data.rows.length === 0 : data.trend.length === 0);

  // Exports whichever view is active — table rows as shown, or the trend
  // series (only arms the analytics layer marked present) per day.
  const handleExport = () => {
    if (!data) return;
    if (view === 'table') {
      if (data.rows.length === 0) return;
      const csvRows = data.rows.map((row) => ({
        day: row.day,
        gmcImpr: formatPct(row.gmcImpr),
        adsImpr: formatPct(row.adsImpr),
        gmcClicks: formatPct(row.gmcClicks),
        adsClicks: formatPct(row.adsClicks),
        convValue: formatPct(row.convValue),
        conversions: formatPct(row.conversions),
        sessions: formatPct(row.sessions),
        revenue: formatPct(row.revenue),
      }));
      downloadCsv('daily-did-lift-table', csvRows, [
        { key: 'day', label: t('detail.dailyLift.columns.day') },
        ...COLUMNS.map((col) => ({
          key: col.key,
          // CSV header names the revenue source too, e.g. "Revenue (Google Ads)".
          label:
            col.key === 'revenue' && data.revenueSource
              ? `${t(col.labelKey)} (${data.revenueSource.source_label})`
              : t(col.labelKey),
        })),
      ]);
    } else {
      if (data.trend.length === 0) return;
      const csvRows = data.trend.map((point) => {
        const row: Record<string, string | number> = { day: point.day };
        presentMeta.forEach((meta) => {
          row[meta.id] = formatChartPct(point[meta.id]);
        });
        return row;
      });
      downloadCsv('daily-did-lift-trend', csvRows, [
        { key: 'day', label: t('detail.dailyLift.columns.day') },
        ...presentMeta.map((meta) => ({
          key: meta.id,
          label: armLabel(meta.id),
        })),
      ]);
    }
  };

  return (
    <Card className="min-w-0 overflow-hidden">
      <CardContent className="min-w-0 space-y-4 p-6">
        {/* Header */}
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3 className="text-[16px] font-medium">{t('detail.dailyLift.title')}</h3>
            <p className="text-muted-foreground text-sm">{t('detail.dailyLift.subtitle')}</p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Button
              variant="outline"
              onClick={handleExport}
              disabled={isExportDisabled}
              className={cn('gap-2', isExportDisabled ? 'cursor-not-allowed' : 'cursor-pointer')}
            >
              <Upload className="h-4 w-4" aria-hidden="true" />
              {t('detail.dailyLift.exportCsv')}
            </Button>
            <Tabs
              value={view}
              onValueChange={(value) => {
                setView(value as ViewMode);
              }}
            >
              <TabsList>
                <TabsTrigger value="trend" className="w-24 cursor-pointer">
                  {t('detail.dailyLift.toggle.trend')}
                </TabsTrigger>
                <TabsTrigger value="table" className="w-24 cursor-pointer">
                  {t('detail.dailyLift.toggle.table')}
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </div>

        {/* Description + (in trend view) series toggles */}
        <div className="flex flex-wrap items-center justify-between gap-4">
          <p className="text-muted-foreground text-sm">{t('detail.dailyLift.description')}</p>
          {view === 'trend' && (
            <div className="flex flex-wrap gap-2">
              {presentMeta.map((meta) => (
                <ExperimentSeriesToggle
                  key={meta.id}
                  color={meta.color}
                  label={armLabel(meta.id)}
                  checked={visibleSeriesIds.includes(meta.id)}
                  onCheckedChange={(v) => {
                    toggleSeries(meta.id, v);
                  }}
                />
              ))}
            </div>
          )}
        </div>

        {isError ? (
          <ExperimentDataUnavailable />
        ) : view === 'trend' ? (
          <>
            {!isLoading && data?.trend.length === 0 ? (
              <ExperimentTrendEmptyState
                title={t('detail.dailyLift.emptyTitle')}
                subtitle={t('detail.dailyLift.emptySubtitle')}
              />
            ) : (
              <Suspense fallback={<Skeleton className="h-80 w-full" />}>
                <ExperimentTrendChart
                  xAxisCategories={trendXAxis}
                  series={trendSeries}
                  visibleSeriesIds={visibleSeriesIds}
                  yAxisFormatter={formatChartPct}
                  isLoading={isLoading}
                />
              </Suspense>
            )}
            <p className="text-muted-foreground text-sm">{t('detail.dailyLift.trendFooter')}</p>
          </>
        ) : !isLoading && data?.rows.length === 0 ? (
          <ExperimentTrendEmptyState
            title={t('detail.dailyLift.emptyTitle')}
            subtitle={t('detail.dailyLift.emptySubtitle')}
          />
        ) : (
          <div className="max-h-100 overflow-auto rounded-md border">
            <table className="w-full min-w-[720px] text-sm">
              <thead className="bg-background sticky top-0 z-10">
                <tr>
                  <th className="text-muted-foreground px-4 py-3 text-left font-medium">
                    {t('detail.dailyLift.columns.day')}
                  </th>
                  {COLUMNS.map((col) => (
                    <th
                      key={col.key}
                      className="text-muted-foreground px-4 py-3 text-left font-medium"
                    >
                      {t(col.labelKey)}
                      {/* Under the GA4 → Google Ads fallback, revenue equals conv. value —
                          the tag says which source this column is. */}
                      {col.key === 'revenue' && (
                        <RevenueSourceTag source={data?.revenueSource} className="ml-1.5" />
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {isLoading || !data
                  ? Array.from({ length: 6 }).map((_, i) => (
                      <tr key={i} className="border-t">
                        <td className="px-4 py-3">
                          <Skeleton className="h-4 w-10" />
                        </td>
                        {COLUMNS.map((col) => (
                          <td key={col.key} className="px-4 py-3">
                            <Skeleton className="h-4 w-12" />
                          </td>
                        ))}
                      </tr>
                    ))
                  : data.rows.map((row) => (
                      <tr key={row.day} className="border-t">
                        <td className="text-muted-foreground px-4 py-3">Day {row.day}</td>
                        {COLUMNS.map((col) => (
                          <td key={col.key} className={cn('px-4 py-3', toneFor(row[col.key]))}>
                            {formatPct(row[col.key])}
                          </td>
                        ))}
                      </tr>
                    ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
